import * as Sentry from '@sentry/react'

const release = `medulla-ui@${import.meta.env.VITE_MEDULLA_VERSION}${
    import.meta.env.VITE_BUILD_NUMBER ? `+${import.meta.env.VITE_BUILD_NUMBER}` : ''
}`

interface SentryConfig {
    sentryDsn?: string
    baseUrl: string
    // k8s namespace, injected by the Helm configmap. Matches the environment tag
    // the Go services and BG send, so a customer's UI and backend errors filter
    // together in Sentry. Optional so a not-yet-redeployed configmap still boots.
    environment?: string
}

const initializeSentry = async () => {
    // We want to make this toggalable, so we can disable Sentry for development or testing
    const response = await fetch(`/config/${import.meta.env.VITE_ALT_BACKEND || 'medulla-ui'}.json`)
    if (response.ok) {
        const sentryConfig = (await response.json()) as SentryConfig

        if (sentryConfig.sentryDsn === undefined) {
            // sentry is not enabled, nothing to do
            return
        }

        // eslint-disable-next-line no-console
        console.info('Sentry enabled')
        Sentry.init({
            release,
            environment: sentryConfig.environment ?? window.location.hostname,
            // public facing dsn
            dsn: sentryConfig.sentryDsn,
            sendDefaultPii: true,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: true,
                    maskAllInputs: true,
                    networkCaptureBodies: false,
                    networkDetailAllowUrls: [sentryConfig.baseUrl],
                }),
            ],
            // minimal trace collections
            tracesSampleRate: 0.05,
            // propagates tracing headers to the backend
            tracePropagationTargets: [
                'localhost',
                /^https:\/\/basal-ganglia\.[a-z0-9-]+\.cyberowl\.io/,
            ],
            // no replays for random sessions
            replaysSessionSampleRate: 0,
            // 1% replays for sessions with errors
            replaysOnErrorSampleRate: 0.01,
        })
    } else {
        throw new Error('Failed to fetch Sentry configuration, sentry is disabled')
    }
}

if (import.meta.env.NODE_ENV !== 'test') {
    try {
        await initializeSentry()
    } catch (err) {
        console.error('Failed to initialize sentry', err)
    }
} else {
    console.error('Sentry disabled for test environments')
}
